import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  List as ListIcon,
  Save as SaveIcon,
  Reply as ReplyIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { getAssociates, createAssociateLedger } from '../../services/associate';
import { getBookings, getCommissionGenerator } from '../../services/project';
import AlertModal from '../../components/AlertModal';

const PaymentAssociate = () => {
  const theme = useTheme();
  const history = useHistory();
  const [formData, setFormData] = useState({
    // voucherNo: '', // Commented out
    associate: null,
    overallCommission: '',
    selectHead: '',
    tds: false,
    tdsPercent: 10,
    tdsAmount: '',
    csrPercent: false,
    csrPercentValue: 0,
    csrAmount: '',
    marketingPercent: false,
    marketingPercentValue: 0,
    marketingAmount: '',
    otherPercent: false,
    otherPercentValue: 0,
    otherAmount: '',
    netAmount: '',
    selectBank: '',
    chequeNo: '',
    booking: null,
    remark: '',
    voucherDate: new Date().toISOString().split('T')[0],
    payableCommission: '',
    paidAmount: '',
    balance: '',
    amount: '',
    charge: false,
    chargeAmount: '',
    chargeAmount2: '',
    paymentMode: '',
    accountNo: '',
    chequeDate: '',
    bookingNo: '',
  });

  // Associates state
  const [associates, setAssociates] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [associatePage, setAssociatePage] = useState(1);
  const [hasMoreAssociates, setHasMoreAssociates] = useState(true);
  const [associateTotalPages, setAssociateTotalPages] = useState(1);

  // Bookings state
  const [bookingOptions, setBookingOptions] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Commission data state
  const [loadingCommission, setLoadingCommission] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);

  // Modal state
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    title: 'Alert',
    message: '',
    type: 'info',
  });

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };

  // Mock banks data
  const banks = [
    'ALLAHABAD BANK',
    'STATE BANK OF INDIA',
    'HDFC BANK',
    'ICICI BANK',
    'AXIS BANK',
    'PUNJAB NATIONAL BANK',
  ];

  // Mock projects data
  const projects = [
    'Green Valley',
    'Sunset Heights',
    'Royal Gardens',
    'Prestige Park',
  ];

  // Mock payment heads
  const paymentHeads = [
    'Commission Paid',
    'Advance Payment',
    // 'Bonus',
    // 'Incentive',
  ];

  // Mock payment modes
  const paymentModes = [
    'Cheque',
    'Cash',
    'Bank Transfer',
    'UPI',
    'NEFT',
    'RTGS',
  ];

  // Load associates
  const loadAssociates = useCallback(async (page = 1, append = false, searchTerm = '') => {
    setLoadingAssociates(true);
    try {
      const response = await getAssociates({ page, limit: 20, search: searchTerm });
      if (response.status && response.data) {
        const formattedAssociates = response.data.map((associate) => ({
          id: associate._id,
          associateId: associate.associateId,
          name: associate.name || '',
          code: associate.associateId || associate.code || '',
          phone: associate.phone || associate.whatsapp || '',
          commission: associate.commission || 0,
          displayLabel: `${associate.name || ''} - ${associate.associateId || associate.code || ''}`,
        }));

        if (append) {
          setAssociates((prev) => [...prev, ...formattedAssociates]);
        } else {
          setAssociates(formattedAssociates);
          setAssociatePage(1);
        }

        // Handle pagination info from extra field
        if (response.extra) {
          const total = parseInt(response.extra.total) || 0;
          const limit = parseInt(response.extra.limit) || 20;
          const totalPages = Math.ceil(total / limit);
          setAssociateTotalPages(totalPages);
          setHasMoreAssociates(page < totalPages);
        } else if (response.pagination) {
          setAssociateTotalPages(response.pagination.totalPages || 1);
          setHasMoreAssociates(page < (response.pagination.totalPages || 1));
        } else {
          setHasMoreAssociates(formattedAssociates.length === 20);
        }
      }
    } catch (error) {
      console.error('Error fetching associates:', error);
      if (!append) {
        setAssociates([]);
      }
    } finally {
      setLoadingAssociates(false);
    }
  }, []);

  // Load initial associates
  useEffect(() => {
    loadAssociates(1, false);
  }, [loadAssociates]);

  // Handle associate search with debounce
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadAssociates(1, false, associateSearchInput);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [associateSearchInput, loadAssociates]);

  // Load more associates when scrolling
  const loadMoreAssociates = useCallback(async () => {
    if (loadingAssociates || !hasMoreAssociates) return;

    const nextPage = associatePage + 1;
    setAssociatePage(nextPage);

    await loadAssociates(nextPage, true, associateSearchInput);
  }, [associatePage, hasMoreAssociates, loadingAssociates, loadAssociates, associateSearchInput]);

  // Handle scroll in associate dropdown - trigger at 80-90% scroll
  const handleAssociateListboxScroll = useCallback((event) => {
    const listboxNode = event.currentTarget;
    const scrollTop = listboxNode.scrollTop;
    const scrollHeight = listboxNode.scrollHeight;
    const clientHeight = listboxNode.clientHeight;

    // Calculate scroll percentage
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when user scrolls to 80-90% of the content
    if (scrollPercentage >= 0.8 && scrollPercentage <= 0.9 && hasMoreAssociates && !loadingAssociates) {
      loadMoreAssociates();
    }
  }, [hasMoreAssociates, loadingAssociates, loadMoreAssociates]);

  // Fetch bookings for selected associate using CommissionGenerator API
  const fetchBookingsList = useCallback(async (associateId) => {
    if (loadingBookings || !associateId) return;

    setLoadingBookings(true);
    try {
      // Call CommissionGenerator API with associateId
      const params = {
        associateId: associateId
      };

      const response = await getCommissionGenerator(params);
      console.log('Commission data for associate:', response);

      // Handle different response structures
      let commissionData = [];
      if (response.success && Array.isArray(response.data)) {
        commissionData = response.data;
      } else if (Array.isArray(response.data)) {
        commissionData = response.data;
      } else if (Array.isArray(response)) {
        commissionData = response;
      }

      // Extract unique bookings from commission data
      // Create a map to store unique bookings by bookingId
      const bookingsMap = new Map();

      commissionData.forEach((item) => {
        // Extract booking information from commission data
        // Commission data should have booking reference - check multiple possible fields
        const bookingIdKey = item.bookingId || item.booking?._id || item.bookingId?._id || item._id || '';
        // Prioritize bookingNo over bookingId for display
        const bookingNo = item.bookingNo || item.booking?.bookingNo || item.booking?.bookingId || item.bookingId || '';

        // Only add if we have a valid booking identifier
        if (bookingIdKey && !bookingsMap.has(bookingIdKey)) {
          const projectName = item.project || item.booking?.particular?.projectName || '';
          const inventoryType = item.unitType || item.booking?.particular?.inventoryType || '';
          const inventoryDetail = item.unitDetail || item.booking?.particular?.inventoryDetail || '';

          // Format: {bookingNo} {projectName} {inventoryType} {inventoryDetail}
          const displayLabel = `Booking No: ${bookingNo} | ${projectName} | ${inventoryType} | ${inventoryDetail}`.trim();

          // Extract projectId directly from the commission item
          const projectId = item.projectId || item.booking?.particular?.projectId || item.booking?.projectId || '';

          bookingsMap.set(bookingIdKey, {
            id: bookingIdKey, // Use bookingId or _id from commission data for API calls
            bookingId: item.bookingId || item.booking?._id || item.bookingId?._id || '',
            bookingNo: bookingNo, // Store bookingNo for display
            projectName: projectName,
            inventoryType: inventoryType,
            inventoryDetail: inventoryDetail,
            displayLabel: displayLabel,
            projectId: projectId, // Store projectId directly
            rawData: item, // Store commission item as rawData
            // Store booking reference if available
            booking: item.booking || null,
            particular: item.booking?.particular || {
              projectName: projectName,
              inventoryType: inventoryType,
              inventoryDetail: inventoryDetail,
              projectId: projectId,
            },
          });
        }
      });

      // Convert map to array
      const formattedBookings = Array.from(bookingsMap.values());

      setBookingOptions(formattedBookings);
    } catch (error) {
      console.error('Error fetching commission data for associate:', error);
      setBookingOptions([]);
    } finally {
      setLoadingBookings(false);
    }
  }, [loadingBookings]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [field]: value,
      };

      // Synchronize paidAmount and amount - they should be the same
      if (field === 'paidAmount') {
        updatedData.amount = value; // Sync amount with paidAmount
      }
      if (field === 'amount') {
        updatedData.paidAmount = value; // Sync paidAmount with amount
      }

      // Auto-calculate balance when payableCommission or paid amount changes
      if (field === 'payableCommission' || field === 'paidAmount' || field === 'amount') {
        const payableCommission = parseFloat(prev.payableCommission) || 0;
        const paid = field === 'paidAmount' || field === 'amount' ? parseFloat(value) || 0 : parseFloat(prev.paidAmount) || 0;

        // Validate: paidAmount/amount cannot be greater than payableCommission
        if (field === 'paidAmount' || field === 'amount') {
          const paidAmount = parseFloat(value) || 0;
          // Skip this validation if Select Head is 'Advance Payment'
          if (updatedData.selectHead !== 'Advance Payment' && paidAmount > payableCommission) {
            // Don't update if paidAmount exceeds payableCommission
            showAlert(`Paid amount cannot be greater than payable commission (₹${payableCommission})`, 'warning', 'Limit Exceeded');
            return prev; // Return previous state without changes
          }
        }

        updatedData.balance = (payableCommission - paid).toFixed(2);
      }

      // Auto-calculate TDS amount when TDS is enabled or TDS percent/amount/paidAmount changes
      if (field === 'tds' || field === 'tdsPercent' || field === 'amount' || field === 'paidAmount') {
        if (updatedData.tds) {
          const amount = field === 'amount' || field === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(updatedData.amount) || 0;
          const tdsPercent = field === 'tdsPercent' ? parseFloat(value) || 10 : parseFloat(updatedData.tdsPercent) || 10;
          const calculatedTds = (amount * tdsPercent) / 100;
          const charge = updatedData.charge ? (parseFloat(updatedData.chargeAmount) || 0) : 0;

          // Validate: TDS + Charge cannot exceed amount
          if (calculatedTds + charge > amount) {
            showAlert(`TDS (₹${calculatedTds.toFixed(2)}) + Charge (₹${charge}) = ₹${(calculatedTds + charge).toFixed(2)} cannot exceed Amount (₹${amount}). Please reduce TDS percentage or charge amount.`, 'warning', 'Invalid Calculation');
            return prev; // Return previous state without changes
          }

          updatedData.tdsAmount = calculatedTds.toFixed(2);
        } else {
          updatedData.tdsAmount = '';
        }
      }

      // Auto-calculate CSR amount when CSR is enabled or CSR percent/amount/paidAmount changes
      if (field === 'csrPercent' || field === 'csrPercentValue' || field === 'amount' || field === 'paidAmount') {
        if (updatedData.csrPercent) {
          const amount = field === 'amount' || field === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(updatedData.amount) || 0;
          const csrPercent = field === 'csrPercentValue' ? parseFloat(value) || 0 : parseFloat(updatedData.csrPercentValue) || 0;
          const calculatedCsr = (amount * csrPercent) / 100;
          updatedData.csrAmount = calculatedCsr.toFixed(2);
        } else {
          updatedData.csrAmount = '';
          updatedData.csrPercentValue = 0;
        }
      }

      // Auto-calculate Marketing amount when Marketing is enabled or Marketing percent/amount/paidAmount changes
      if (field === 'marketingPercent' || field === 'marketingPercentValue' || field === 'amount' || field === 'paidAmount') {
        if (updatedData.marketingPercent) {
          const amount = field === 'amount' || field === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(updatedData.amount) || 0;
          const marketingPercent = field === 'marketingPercentValue' ? parseFloat(value) || 0 : parseFloat(updatedData.marketingPercentValue) || 0;
          const calculatedMarketing = (amount * marketingPercent) / 100;
          updatedData.marketingAmount = calculatedMarketing.toFixed(2);
        } else {
          updatedData.marketingAmount = '';
          updatedData.marketingPercentValue = 0;
        }
      }

      // Auto-calculate Other amount when Other is enabled or Other percent/amount/paidAmount changes
      if (field === 'otherPercent' || field === 'otherPercentValue' || field === 'amount' || field === 'paidAmount') {
        if (updatedData.otherPercent) {
          const amount = field === 'amount' || field === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(updatedData.amount) || 0;
          const otherPercent = field === 'otherPercentValue' ? parseFloat(value) || 0 : parseFloat(updatedData.otherPercentValue) || 0;
          const calculatedOther = (amount * otherPercent) / 100;
          updatedData.otherAmount = calculatedOther.toFixed(2);
        } else {
          updatedData.otherAmount = '';
          updatedData.otherPercentValue = 0;
        }
      }

      // Auto-calculate net amount when amount/paidAmount, charge, TDS, CSR, Marketing, or Other changes
      // netAmount = amount - charge - tds - csr - marketing - other
      if (field === 'amount' || field === 'paidAmount' || field === 'chargeAmount' || field === 'charge' ||
        field === 'tds' || field === 'tdsPercent' || field === 'tdsAmount' ||
        field === 'csrPercent' || field === 'csrPercentValue' || field === 'csrAmount' ||
        field === 'marketingPercent' || field === 'marketingPercentValue' || field === 'marketingAmount' ||
        field === 'otherPercent' || field === 'otherPercentValue' || field === 'otherAmount') {
        const amount = field === 'amount' || field === 'paidAmount' ? parseFloat(value) || 0 : parseFloat(updatedData.amount) || 0;
        const charge = updatedData.charge ? (field === 'chargeAmount' ? parseFloat(value) || 0 : parseFloat(updatedData.chargeAmount) || 0) : 0;
        const tds = updatedData.tds ? (parseFloat(updatedData.tdsAmount) || 0) : 0;
        const csr = updatedData.csrPercent ? (parseFloat(updatedData.csrAmount) || 0) : 0;
        const marketing = updatedData.marketingPercent ? (parseFloat(updatedData.marketingAmount) || 0) : 0;
        const other = updatedData.otherPercent ? (parseFloat(updatedData.otherAmount) || 0) : 0;

        // Validate: charge + TDS + CSR + Marketing + Other cannot exceed amount (net amount cannot be negative)
        const totalDeductions = charge + tds + csr + marketing + other;
        if (totalDeductions > amount) {
          // Don't update if total deductions would exceed amount
          showAlert(`Charge Amount Cannot Exceed Net amount.`, 'warning', 'Limit Exceeded');
          return prev; // Return previous state without changes
        }

        updatedData.netAmount = (amount - charge - tds - csr - marketing - other).toFixed(2);
      }

      return updatedData;
    });
  };

  // Handle associate change - fetch bookings for selected associate
  const handleAssociateChange = useCallback(async (event, newValue) => {
    // Update associate and overall commission
    setFormData((prev) => {
      const updated = {
        ...prev,
        associate: newValue,
        overallCommission: newValue && newValue.commission ? newValue.commission.toString() : '',
        booking: null, // Clear booking when associate changes
      };

      return updated;
    });

    // Clear booking options
    setBookingOptions([]);

    // If associate is selected, fetch bookings for that associate
    if (newValue && newValue.id) {
      try {
        await fetchBookingsList(newValue.id);
      } catch (error) {
        console.error('Error fetching bookings for associate:', error);
        setBookingOptions([]);
      }
    }
  }, [fetchBookingsList]);

  // Handler for Booking selection
  const handleBookingChange = useCallback(async (event, value) => {
    setFormData((prev) => ({ ...prev, booking: value }));

    // Call getCommissionGenerator API when booking is selected
    if (value && value.id) {
      setLoadingCommission(true);
      try {
        const params = { bookingId: value.id, available: "true" };

        // Add associateId if associate is selected
        if (formData.associate && formData.associate.id) {
          params.associateId = formData.associate.id;
        }

        const response = await getCommissionGenerator(params);
        console.log('Commission data:', response);

        // Extract projectId from commission response and update booking
        let projectIdFromResponse = '';
        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          projectIdFromResponse = response.data[0].projectId || '';
        }

        // Map totalPayableCommissionAmount to Payable Commission and update projectId
        if (response.success && response.totalPayableCommissionAmount !== undefined) {
          setFormData((prev) => {
            const updated = {
              ...prev,
              payableCommission: response.totalPayableCommissionAmount.toString(),
            };

            // Update booking with projectId from commission response
            if (projectIdFromResponse && prev.booking) {
              updated.booking = {
                ...prev.booking,
                projectId: projectIdFromResponse,
                rawData: {
                  ...prev.booking.rawData,
                  projectId: projectIdFromResponse,
                },
              };
            }

            // Recalculate balance when payableCommission is updated
            if (updated.payableCommission || prev.paidAmount) {
              const commission = parseFloat(updated.payableCommission) || 0;
              const paid = parseFloat(prev.paidAmount) || 0;
              updated.balance = (commission - paid).toString();
            }

            return updated;
          });
        }
      } catch (error) {
        console.error('Error fetching commission data:', error);
        // Optionally show an error message to the user
      } finally {
        setLoadingCommission(false);
      }
    }
  }, [formData.associate]);

  const handleSave = async () => {
    // Validate required fields
    if (!formData.associate || !formData.associate.id) {
      showAlert('Please select an associate', 'warning', 'Required');
      return;
    }

    if (!formData.booking || !formData.booking.id) {
      showAlert('Please select a booking', 'warning', 'Required');
      return;
    }

    if (!formData.selectHead) {
      showAlert('Please select a head', 'warning', 'Required');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showAlert('Please enter a valid amount', 'warning', 'Required');
      return;
    }

    if (!formData.paymentMode) {
      showAlert('Please select a payment mode', 'warning', 'Required');
      return;
    }

    if (!formData.voucherDate) {
      showAlert('Please select a voucher date', 'warning', 'Required');
      return;
    }

    setSaving(true);
    try {
      // Map form data to API payload
      const payload = {
        associateId: formData.associate.id,
        bookingId: formData.booking.id,
        projectId: formData.booking.projectId || formData.booking.rawData?.projectId || formData.booking.particular?.projectId || '',
        transactionType: 'deduct',
        amount: parseFloat(formData.amount) || 0,
        tdsPercent: formData.tds ? (parseFloat(formData.tdsPercent) || 0) : 0,
        csrPercent: formData.csrPercent ? (parseFloat(formData.csrPercentValue) || 0) : 0,
        csrAmount: formData.csrPercent ? (parseFloat(formData.csrAmount) || 0) : 0,
        marketingPercent: formData.marketingPercent ? (parseFloat(formData.marketingPercentValue) || 0) : 0,
        marketingAmount: formData.marketingPercent ? (parseFloat(formData.marketingAmount) || 0) : 0,
        otherPercent: formData.otherPercent ? (parseFloat(formData.otherPercentValue) || 0) : 0,
        otherAmount: formData.otherPercent ? (parseFloat(formData.otherAmount) || 0) : 0,
        charge: formData.charge ? (parseFloat(formData.chargeAmount) || 0) : 0,
        paymentHead: formData.selectHead || '',
        paymentMode: formData.paymentMode || '',
        bankName: formData.selectBank || '',
        accountNumber: formData.accountNo || '',
        chequeNumber: formData.chequeNo || '',
        remark: formData.remark || '',
        date: formData.voucherDate || '',
        chequeDate: formData.chequeDate || '',
      };

      // Debug logs
      // console.log('Form Data - selectBank:', formData.selectBank);
      // console.log('Form Data - accountNo:', formData.accountNo);
      // console.log('Form Data - chequeNo:', formData.chequeNo);
      // console.log('Payload:', payload);

      const response = await createAssociateLedger(payload);
      console.log('Associate ledger created:', response);

      // Show success message
      showAlert('Payment voucher saved successfully!', 'success', 'Success');

      // Reset form fields
      setFormData({
        associate: null,
        overallCommission: '',
        selectHead: '',
        tds: false,
        tdsPercent: 10,
        tdsAmount: '',
        csrPercent: false,
        csrPercentValue: 0,
        csrAmount: '',
        marketingPercent: false,
        marketingPercentValue: 0,
        marketingAmount: '',
        otherPercent: false,
        otherPercentValue: 0,
        otherAmount: '',
        netAmount: '',
        selectBank: '',
        chequeNo: '',
        booking: null,
        remark: '',
        voucherDate: new Date().toISOString().split('T')[0],
        payableCommission: '',
        paidAmount: '',
        balance: '',
        amount: '',
        charge: false,
        chargeAmount: '',
        chargeAmount2: '',
        paymentMode: '',
        accountNo: '',
        chequeDate: '',
        bookingNo: '',
      });

      // Clear selection states
      setBookingOptions([]);
    } catch (error) {
      console.error('Failed to save payment voucher:', error);
      const errorMessage = error.message || error.response?.data?.message || 'Failed to save payment voucher';
      showAlert(`Error: ${errorMessage}`, 'error', 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleList = () => {
    history.push('/app/paymentAssociate/List');
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, pb: 10, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
              Payment Voucher
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Home Page / Payment Voucher
            </Typography> */}
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ListIcon />}
                onClick={handleList}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                }}
              >
                List
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Payment Form */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#000', mb: 0 }}>
              Payment
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => history.push('/app/pay-incentive')}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: theme.palette.primary.main,
                boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}40`,
                '&:hover': { 
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: `0 6px 16px 0 ${theme.palette.primary.main}50`
                }
              }}
            >
              Pay Incentive
            </Button>
          </Box>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {/* Associate and Select Booking side by side */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={associates}
                    getOptionLabel={(option) => option.displayLabel || `${option.name || ''} - ${option.code || ''}`}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    loading={loadingAssociates}
                    value={formData.associate}
                    onChange={handleAssociateChange}
                    ListboxProps={{
                      onScroll: handleAssociateListboxScroll,
                      style: { maxHeight: '300px' },
                    }}
                    inputValue={associateSearchInput}
                    onInputChange={(_, newInputValue) => setAssociateSearchInput(newInputValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Associate *"
                        size="small"
                        placeholder="Search Associate"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loadingAssociates ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                          {option.code}
                        </Typography>
                      </Box>
                    )}
                    noOptionsText={loadingAssociates ? 'Loading associates...' : 'No associates found'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={bookingOptions}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.displayLabel || `Booking ${option.bookingId || ''}`;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    loading={loadingBookings}
                    value={formData.booking}
                    onChange={handleBookingChange}
                    disabled={!formData.associate || !formData.associate.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Booking *"
                        placeholder={formData.associate ? "Select Booking *" : "Select Associate first"}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingBookings ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={
                      loadingBookings ? 'Loading bookings...' : formData.associate ? 'No bookings found for this associate' : 'Please select an associate first'
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body2">
                          {option.displayLabel}
                        </Typography>
                      </Box>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Overall Commission"
                    value={formData.overallCommission}
                    onChange={(e) => handleInputChange('overallCommission', e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">%</InputAdornment>,
                    }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="select-head-label">Payment Head *</InputLabel>
                    <Select
                      labelId="select-head-label"
                      value={formData.selectHead}
                      label="Payment Head *"
                      required
                      onChange={(e) => handleInputChange('selectHead', e.target.value)}
                    >
                      {paymentHeads.map((head) => (
                        <MenuItem key={head} value={head}>
                          {head}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.tds}
                        onChange={(e) => handleInputChange('tds', e.target.checked)}
                      />
                    }
                    label="TDS"
                  />
                </Grid>
                {formData.tds && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="TDS %"
                          type="number"
                          value={formData.tdsPercent}
                          onChange={(e) => handleInputChange('tdsPercent', e.target.value)}
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="TDS Amount"
                          value={formData.tdsAmount}
                          onChange={(e) => handleInputChange('tdsAmount', e.target.value)}
                          size="small"
                          InputProps={{
                            readOnly: true,
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.csrPercent}
                        onChange={(e) => handleInputChange('csrPercent', e.target.checked)}
                      />
                    }
                    label="CSR Percent"
                  />
                </Grid>
                {formData.csrPercent && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="CSR %"
                          type="number"
                          value={formData.csrPercentValue}
                          onChange={(e) => handleInputChange('csrPercentValue', e.target.value)}
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="CSR Amount"
                          value={formData.csrAmount}
                          onChange={(e) => handleInputChange('csrAmount', e.target.value)}
                          size="small"
                          InputProps={{
                            readOnly: true,
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.marketingPercent}
                        onChange={(e) => handleInputChange('marketingPercent', e.target.checked)}
                      />
                    }
                    label="Marketing Percent"
                  />
                </Grid>
                {formData.marketingPercent && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Marketing %"
                          type="number"
                          value={formData.marketingPercentValue}
                          onChange={(e) => handleInputChange('marketingPercentValue', e.target.value)}
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Marketing Amount"
                          value={formData.marketingAmount}
                          onChange={(e) => handleInputChange('marketingAmount', e.target.value)}
                          size="small"
                          InputProps={{
                            readOnly: true,
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.otherPercent}
                        onChange={(e) => handleInputChange('otherPercent', e.target.checked)}
                      />
                    }
                    label="Other Percent"
                  />
                </Grid>
                {formData.otherPercent && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Other %"
                          type="number"
                          value={formData.otherPercentValue}
                          onChange={(e) => handleInputChange('otherPercentValue', e.target.value)}
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Other Amount"
                          value={formData.otherAmount}
                          onChange={(e) => handleInputChange('otherAmount', e.target.value)}
                          size="small"
                          InputProps={{
                            readOnly: true,
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Net Amount"
                    value={formData.netAmount}
                    onChange={(e) => handleInputChange('netAmount', e.target.value)}
                    size="small"
                    error={parseFloat(formData.netAmount || 0) < 0}
                    helperText={
                      parseFloat(formData.netAmount || 0) < 0
                        ? 'Net amount cannot be negative. Please reduce charge or TDS amount.'
                        : ''
                    }
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                {formData.paymentMode !== 'Cash' && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Bank</InputLabel>
                        <Select
                          value={formData.selectBank}
                          label="Select Bank"
                          onChange={(e) => handleInputChange('selectBank', e.target.value)}
                        >
                          {banks.map((bank) => (
                            <MenuItem key={bank} value={bank}>
                              {bank}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Cheque No"
                        value={formData.chequeNo}
                        onChange={(e) => handleInputChange('chequeNo', e.target.value)}
                        size="small"
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remark"
                    multiline
                    rows={3}
                    value={formData.remark}
                    onChange={(e) => handleInputChange('remark', e.target.value)}
                    placeholder="Enter Remark"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Voucher Date"
                    type="date"
                    required
                    value={formData.voucherDate}
                    onChange={(e) => handleInputChange('voucherDate', e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Payable Commission"
                    value={formData.payableCommission}
                    onChange={(e) => handleInputChange('payableCommission', e.target.value)}
                    size="small"
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Paid Amount"
                    value={formData.paidAmount}
                    onChange={(e) => handleInputChange('paidAmount', e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    error={formData.selectHead !== 'Advance Payment' && parseFloat(formData.paidAmount || 0) > parseFloat(formData.payableCommission || 0)}
                    helperText={
                      formData.selectHead !== 'Advance Payment' && parseFloat(formData.paidAmount || 0) > parseFloat(formData.payableCommission || 0)
                        ? `Paid amount cannot exceed payable commission (₹${formData.payableCommission || 0})`
                        : ''
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          ₹ {formData.selectHead === 'Advance Payment' && parseFloat(formData.payableCommission || 0) === 0 ? '-' : ''}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Balance"
                    value={formData.balance}
                    onChange={(e) => handleInputChange('balance', e.target.value)}
                    size="small"
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                {/* <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Amount"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Grid> */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.charge}
                          onChange={(e) => handleInputChange('charge', e.target.checked)}
                        />
                      }
                      label="Charge"
                    />
                    <TextField
                      size="small"
                      value={formData.chargeAmount}
                      onChange={(e) => handleInputChange('chargeAmount', e.target.value)}
                      sx={{ width: 120 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    />
                  </Box>
                  {/* <TextField
                      fullWidth
                      size="small"
                      value={formData.chargeAmount2}
                      onChange={(e) => handleInputChange('chargeAmount2', e.target.value)}
                      sx={{ mt: 1 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      }}
                    /> */}
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Mode</InputLabel>
                    <Select
                      value={formData.paymentMode}
                      label="Payment Mode"
                      onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                    >
                      {paymentModes.map((mode) => (
                        <MenuItem key={mode} value={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.paymentMode !== 'Cash' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Account No"
                        value={formData.accountNo}
                        onChange={(e) => handleInputChange('accountNo', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Cheque Date"
                        type="date"
                        value={formData.chequeDate}
                        onChange={(e) => handleInputChange('chequeDate', e.target.value)}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}
                {/* <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Booking No"
                      value={formData.bookingNo}
                      onChange={(e) => handleInputChange('bookingNo', e.target.value)}
                      size="small"
                    />
                  </Grid> */}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </Box>
  );
};

export default PaymentAssociate;
